import type { AxiosResponse } from 'axios';

/**
 * Unwrap the `data` envelope every partner endpoint returns, coercing to the
 * shape the caller expects.
 *
 * Reading `res.data.data` straight into state was safe against a FAILED
 * request, because every .catch() here substitutes an empty value. It was not
 * safe against a malformed SUCCESS: a 200 whose body has no `data` key stored
 * `undefined`, and the first render that treated it as a list took the whole
 * page down rather than emptying one card.
 *
 * That is not hypothetical. A 200 carrying HTML instead of JSON is what a
 * misdirected VITE_API_BASE_URL, an SPA fallback, or a proxy error page all
 * look like from here, and it produced:
 *
 *   Uncaught TypeError: Cannot read properties of undefined (reading 'length')
 *     at completeMonths (reportPeriods.ts:10)
 *
 * Coercing at this boundary keeps a bad payload confined to the card that
 * asked for it. Callers still get their empty state, and the failure stays
 * visible through the toast the .catch() raises for real errors.
 */
export function listPayload<T>(response: AxiosResponse): T[] {
  const data = response?.data?.data;

  return Array.isArray(data) ? (data as T[]) : [];
}

/**
 * The object counterpart, for the endpoints that return a record rather than
 * a list (summary, outstanding receivables, trial conversion). Returns null
 * rather than an empty object, because every caller already renders a null
 * as "no data" and an empty object would read as a record whose fields are
 * all undefined.
 *
 * An array is explicitly rejected: it is the wrong shape here, and it is
 * truthy, which is exactly how an earlier catch-all mock walked a page into
 * `undefined.toLocaleString()`.
 */
export function objectPayload<T>(response: AxiosResponse): T | null {
  const data = response?.data?.data;

  if (data === null || data === undefined || typeof data !== 'object' || Array.isArray(data)) {
    return null;
  }

  return data as T;
}
